import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';

import '../App.css';
import Controls from './Controls';
import VerseDisplay from './VerseDisplay';

function BibleView() {
    const [books, setBooks] = useState([]);

    const [chapters, setChapters] = useState([]);
    const [selectedChapter, setSelectedChapter] = useState('');
    const [verses, setVerses] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [highlightedVerse, setHighlightedVerse] = useState(null);
    const verseRefs = useRef({});
    const navigate = useNavigate();
    const location = useLocation();
    const { bookId, chapterNumber } = useParams();
    const selectedBook = books.find(b => b.book_number === parseInt(bookId));

    useEffect(() => {
        axios.get('/api/bible/books').then(response => {
            console.log('books', response.data);
            setBooks(response.data);
        });
    }, []);

    useEffect(() => {
        const currentBookId = bookId || (books.length > 0 && books[0].book_number);
        if (!currentBookId) return;

        axios.get(`/api/bible/books/${currentBookId}/chapters`).then(response => {
            console.log('chapters', response.data);
            const fetchedChapters = response.data;
            setChapters(fetchedChapters);

            const currentChapter = chapterNumber && fetchedChapters.includes(parseInt(chapterNumber))
                ? parseInt(chapterNumber)
                : fetchedChapters[0];

            if (currentChapter && selectedChapter !== currentChapter) {
                setSelectedChapter(currentChapter);
            }

            if (currentBookId && currentChapter) {
                axios.get(`/api/bible/books/${currentBookId}/${currentChapter}/verses`).then(response => {
                    setVerses(response.data);
                    const newPath = `/bible/${currentBookId}/${currentChapter}${location.hash}`;
                    if (location.pathname + location.hash !== newPath) {
                        navigate(newPath, { replace: true });
                    }
                });
            }
        });
    }, [bookId, chapterNumber, books, navigate, location.pathname, location.hash]);

    useEffect(() => {
        const hash = location.hash.replace('#', '');
        const verseId = hash ? parseInt(hash) : null;
        if (highlightedVerse !== verseId) {
            setHighlightedVerse(verseId);
        }
    }, [location.hash, highlightedVerse]);

    useEffect(() => {
        if (highlightedVerse && verseRefs.current[highlightedVerse]) {
            setTimeout(() => {
                verseRefs.current[highlightedVerse]?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                });
            }, 100);
        } else {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }, [highlightedVerse, selectedChapter, verses]);

    const handleBookChange = (event) => {
        handleClearSearch();
        navigate(`/bible/${event.target.value}/1`);
    };

    const handleChapterChange = (event) => {
        handleClearSearch();
        navigate(`/bible/${bookId}/${event.target.value}`);
    };

    const handlePreviousChapter = () => {
        handleClearSearch();
        const newChapter = selectedChapter - 1;
navigate(`/bible/${bookId}/${newChapter}`);
    };

    const handleNextChapter = () => {
        handleClearSearch();
        const newChapter = selectedChapter + 1;
        if (newChapter <= chapters.length) {
            navigate(`/bible/${bookId}/${newChapter}`);
        }
    };

    const handleSearchChange = (event) => {
        setSearchQuery(event.target.value);
    };

    const handleSearchSubmit = (event) => {
        event.preventDefault();
        if (searchQuery.trim() !== '') {
            axios.get(`/api/bible/verses/search?q=${searchQuery}`)
                .then(response => {
                    const results = response.data.map(r => ({
                        book: { name: r.long_name, id: r.book_number },
                        chapter: r.chapter,
                        verse: r.verse,
                        highlighted_text: r.highlighted_text
                    }));
                    setSearchResults(results);
                    setIsSearching(true);
                })
                .catch(error => {
                    console.error('Error searching verses:', error);
                });
        }
    };

    const handleSearchResultClick = (result) => {
        setIsSearching(false);
        setHighlightedVerse(result.verse);
        navigate(`/bible/${result.book.id}/${result.chapter}#${result.verse}`);
    };

    const handleVerseClick = (verse) => {
        navigate(`/bible/${bookId}/${chapterNumber}#${verse.verse}`);
    };

    const handleClearSearch = () => {
        setSearchQuery('');
        setSearchResults([]);
        setIsSearching(false);
    };

    return (
        <div className="container mt-3 book-page-width">
            <Controls
                books={books}
                selectedBook={selectedBook}
                bookId={bookId}
                handleBookChange={handleBookChange}
                chapters={chapters}
                selectedChapter={selectedChapter}
                handleChapterChange={handleChapterChange}
                handlePreviousChapter={handlePreviousChapter}
                handleNextChapter={handleNextChapter}
                searchQuery={searchQuery}
                handleSearchChange={handleSearchChange}
                handleSearchSubmit={handleSearchSubmit}
                showSearch={true}
            />
            <VerseDisplay
                isSearching={isSearching}
                searchResults={searchResults}
                handleSearchResultClick={handleSearchResultClick}
                verses={verses}
                highlightedVerse={highlightedVerse}
                verseRefs={verseRefs}
                searchQuery={searchQuery}
                handleVerseClick={handleVerseClick}
            />
            <Controls
                books={books}
                selectedBook={selectedBook}
                bookId={bookId}
                handleBookChange={handleBookChange}
                chapters={chapters}
                selectedChapter={selectedChapter}
                handleChapterChange={handleChapterChange}
                handlePreviousChapter={handlePreviousChapter}
                handleNextChapter={handleNextChapter}
            />
        </div>
    );
}

export default BibleView;
